import React, { useEffect, useMemo, useState, useRef, forwardRef, useImperativeHandle, useCallback } from 'react';
import { Save, RotateCcw, Trash2, Plus } from 'lucide-react';
import { message } from 'antd';
import i18next, { t } from 'i18next';
import {
  sessionOptions, keyLevelTypeOptions, type KeyLevel, type MoodTone,
  preTradeStates, moodQuick, moodPositive, moodNegative, moodDanger, preStateWarnings
} from 'mock/journalDay.data';
import { C, CollapsibleSection, StatusChip, Pill, Divider, JdSelect, FieldLabel, TextInput, TextArea, PillTone } from '../../components/ui';
import { DynamicChecklists, FormFieldConfig, Journal, UILabels_PreTrade, DailyPlanData, PhysicalHealthTags, RatingLevel, UpdateDailyJournalPayload, UILabels_DailyPlan, StepRefHandle, SectionHandle, IncompleteSection } from './type';
import { getMostCompleteExecution, getExecutionCompletenessScore } from './function';
import { getFormConfigsField, uploadFieldData_daily_plan_data, mapTCApiToUi, mapTCUiToApi } from './utility_function';
import { updateDailyChecklist } from './function';
import { API_URL } from 'env';

type Dir = string | null;
type Clean = string | null;

export interface OptionType {
  label: string;
  value: string;
  type?: 'success' | 'warning' | 'danger' | 'info' | 'good' | 'bad' | 'warn' | string;
}

// 1. Helper lấy options đa ngôn ngữ chuẩn hóa, đồng thời giữ lại thuộc tính type / kind
function getOptionsFromConfig(
  configs: FormFieldConfig[],
  name: string,
  fallback: OptionType[] = [],
  currentLang: string = 'vi'
): OptionType[] {
  const found = configs?.find(c => c.field_key === name || (c as any).name === name);

  if (found && Array.isArray(found.options) && found.options.length > 0) {
    return found.options.map((opt: any) => {
      if (typeof opt === 'string') {
        return { label: opt, value: opt, type: 'neutral' };
      }

      const rawValue = opt.value ?? opt.id ?? opt.code ?? opt.label ?? opt;
      const value = typeof rawValue === 'object' && rawValue !== null
        ? rawValue[currentLang] || rawValue['vi'] || rawValue['en'] || Object.values(rawValue)[0]
        : String(rawValue);

      let label = '';
      if (currentLang === 'en') {
        label = opt.label_en || opt.label_vi;
      } else {
        label = opt.label_vi || opt.label_en;
      }

      if (!label) {
        if (typeof opt.label === 'object' && opt.label !== null) {
          label = opt.label[currentLang] || opt.label['vi'] || opt.label['en'] || Object.values(opt.label)[0];
        } else {
          label = opt.label || value;
        }
      }

      // Lấy type/kind trực tiếp từ Option trong config
      const optType = opt.type || opt.kind || opt.status || 'neutral';

      return {
        label: String(label),
        value: String(value),
        type: String(optType).toLowerCase(),
      };
    });
  }

  return fallback;
}

// 2. Helper ánh xạ option.type ("success", "warning", "danger", ...) sang tone của component Pill
function getToneFromOptionType(type?: string): PillTone {
  if (!type) return 'neutral' as PillTone;
  const tLower = type.toLowerCase();

  if (tLower === 'success' || tLower === 'good' || tLower === 'buy' || tLower === 'pos' || tLower === 'ok') {
    return 'buy' as PillTone; // Mạch màu xanh
  }
  if (tLower === 'danger' || tLower === 'bad' || tLower === 'sell' || tLower === 'neg' || tLower === 'error') {
    return 'sell' as PillTone; // Mạch màu đỏ
  }
  if (tLower === 'warning' || tLower === 'warn' || tLower === 'caution') {
    // Nếu PillTone trong ui.tsx dùng "gold" hoặc "warning" thay vì "warn", hãy đổi chuỗi tương ứng ở đây:
    return 'neutral' as PillTone; // Hoặc 'gold' / 'warning' tùy thuộc vào các value được hỗ trợ trong PillTone
  }
  return 'neutral' as PillTone;
}

// Helper lấy màu sắc chi tiết cho Style theo Option Type
function getOptionColorsByType(type?: string, isSelected: boolean = false) {
  const tone = getToneFromOptionType(type);
  if (tone === 'buy') {
    return {
      bd: C.green,
      bg: isSelected ? C.greenBg : C.surface,
      tx: isSelected ? C.greenTx : C.text,
      dot: C.green
    };
  }
  if (tone === 'sell') {
    return {
      bd: C.red,
      bg: isSelected ? C.redBg : C.surface,
      tx: isSelected ? C.redTx : C.text,
      dot: C.red
    };
  }
  if (tone === 'neutral') {
    return {
      bd: C.gold,
      bg: isSelected ? C.goldBg : C.surface,
      tx: isSelected ? C.goldDark : C.text,
      dot: C.gold
    };
  }
  return {
    bd: C.chipOffBd,
    bg: isSelected ? C.neutralBg : C.surface,
    tx: isSelected ? C.title : C.chipOffTx,
    dot: C.faint
  };
}

// Verdict banner
interface VerdictBannerProps {
  execution?: any;
  dynamic_checklist?: DynamicChecklists;
}

function VerdictBanner({ execution, dynamic_checklist }: VerdictBannerProps) {
  const { total: totalScore, market: marketScore, setup: setupScore, risk: riskScore, psych: psychScore } = getExecutionCompletenessScore(execution, dynamic_checklist);
  const dpct = totalScore / 100;

  const v = totalScore >= 85
    ? {
      accent: '#3C8C5B', bg: '#E6F3EA', border: '#B7DDC4',
      label: 'SẴN SÀNG', title: 'Đủ điều kiện vào lệnh', desc: 'Tất cả các trụ cột đạt ngưỡng an toàn. Bạn có thể vào lệnh theo đúng kế hoạch đã chuẩn bị.',
      cta: 'Xác nhận vào lệnh', ctaBg: '#3C8C5B', ctaTx: '#FFFFFF',
      chipBg: 'rgba(60,140,91,0.12)', chipBd: 'rgba(60,140,91,0.25)', chipTx: '#3C8C5B', glow: 'rgba(255,255,255,0.4)'
    }
    : totalScore >= 60
      ? {
        accent: '#D98C2B', bg: '#FCF3E4', border: '#EFDBB2',
        label: 'CẨN TRỌNG', title: 'Cân nhắc kỹ trước khi vào lệnh', desc: 'Một vài trụ cột chưa tối ưu. Cân nhắc giảm khối lượng hoặc chờ thêm xác nhận trước khi vào lệnh.',
        cta: 'Xem điểm yếu', ctaBg: '#D98C2B', ctaTx: '#FFFFFF',
        chipBg: 'rgba(217,140,43,0.15)', chipBd: 'rgba(217,140,43,0.3)', chipTx: '#A66A1F', glow: 'rgba(255,255,255,0.4)'
      }
      : {
        accent: '#D98C2B', bg: '#FCF3E4', border: '#EFDBB2',
        label: 'RỦI RO CAO', title: 'Chưa nên vào lệnh', desc: 'Nhiều trụ cột chưa đạt ngưỡng. Khuyến nghị đứng ngoài và hoàn thiện kế hoạch trước khi giao dịch.',
        cta: 'Hoàn thiện kế hoạch', ctaBg: '#C1493B', ctaTx: '#FFFFFF',
        chipBg: 'rgba(217,140,43,0.15)', chipBd: 'rgba(217,140,43,0.3)', chipTx: '#A66A1F', glow: 'rgba(255,255,255,0.4)'
      };

  return (
    <section style={{ position: 'relative', borderRadius: 18, overflow: 'hidden', background: v.bg, border: `1px solid ${v.border}`, borderLeft: `6px solid ${v.accent}`, boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
      <div style={{ position: 'absolute', right: '10%', top: '50%', transform: 'translateY(-50%)', width: 300, height: 300, borderRadius: '50%', background: `radial-gradient(circle,${v.glow},transparent 70%)`, filter: 'blur(20px)', pointerEvents: 'none' }} />
      <div style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 26, padding: '24px 28px', flexWrap: 'wrap' }}>
        <div style={{ position: 'relative', width: 90, height: 90, flex: 'none', borderRadius: '50%', background: `conic-gradient(${v.accent} ${Math.round(dpct * 360)}deg, rgba(31,36,32,0.08) 0)`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ width: 72, height: 72, borderRadius: '50%', background: '#FFFFFF', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 5px rgba(0,0,0,0.05)' }}>
            <span style={{ fontSize: 24, fontWeight: 800, color: '#1F2420', lineHeight: 1 }}>{totalScore}</span>
            <span style={{ fontSize: 11, fontWeight: 600, color: '#9A9D97' }}>/ 100</span>
          </div>
        </div>
        <div style={{ flex: 1, minWidth: 240 }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 7, padding: '5px 12px', borderRadius: 999, marginBottom: 10, background: v.chipBg, border: `1px solid ${v.chipBd}` }}>
            <span style={{ width: 6, height: 6, borderRadius: '50%', background: v.chipTx }} />
            <span style={{ fontSize: 12, fontWeight: 500, letterSpacing: 0.5, color: v.chipTx }}>{v.label}</span>
          </div>
          <div style={{ fontSize: 19, fontWeight: 500, color: '#1F2420', marginBottom: 4 }}>{v.title}</div>
          <div style={{ fontSize: 13, color: '#6B6F6A', lineHeight: 1.6, maxWidth: 560 }}>{v.desc}</div>
        </div>
        <div style={{ flex: 'none', display: 'flex', flexDirection: 'column', gap: 9 }}>
          <button style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8, padding: '11px 22px', border: 'none', borderRadius: 10, background: v.accent, color: '#FFFFFF', fontFamily: 'inherit', fontSize: 13.5, fontWeight: 500, cursor: 'pointer', boxShadow: `0 4px 12px ${v.accent}4D` }}>{v.cta}</button>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
            <span title="Context" style={{ fontSize: 11, fontWeight: 500, color: marketScore >= 16 ? '#3C8C5B' : marketScore >= 12 ? '#D98C2B' : '#C1493B' }}>{marketScore}</span>
            <span style={{ fontSize: 11, color: '#D4D6D2' }}>•</span>
            <span title="Setup" style={{ fontSize: 11, fontWeight: 500, color: setupScore >= 24 ? '#3C8C5B' : setupScore >= 18 ? '#D98C2B' : '#C1493B' }}>{setupScore}</span>
            <span style={{ fontSize: 11, color: '#D4D6D2' }}>•</span>
            <span title="Risk" style={{ fontSize: 11, fontWeight: 500, color: riskScore >= 28 ? '#3C8C5B' : riskScore >= 21 ? '#D98C2B' : '#C1493B' }}>{riskScore}</span>
            <span style={{ fontSize: 11, color: '#D4D6D2' }}>•</span>
            <span title="Psych" style={{ fontSize: 11, fontWeight: 500, color: psychScore >= 12 ? '#3C8C5B' : psychScore >= 9 ? '#D98C2B' : '#C1493B' }}>{psychScore}</span>
          </div>
        </div>
      </div>
    </section>
  );
}

// Section 1 — Bối cảnh thị trường
interface MarketSectionProps {
  date: string;
  account_number: string;
  symbol: string;
  journal_data: Journal;
  form_configs: FormFieldConfig[];
  ui_labels: UILabels_PreTrade["step_1"];
  onReload?: () => void;
  open: boolean;
  can_edit?: boolean;
  onToggle: () => void;
  onSaveSuccess?: () => void;
  sectionRef?: React.MutableRefObject<SectionHandle | null>;
}

function MarketSection({ date, account_number, symbol, journal_data, form_configs, ui_labels, onReload, open, can_edit, onToggle, onSaveSuccess, sectionRef }: MarketSectionProps) {
  const currentLanguage = i18next.language;

  const [dir, setDir] = useState<Dir>(null);
  const [reason, setReason] = useState('');
  const [fromDayPlan, setFromDayPlan] = useState(false);
  const [scenarioNote, setScenarioNote] = useState('');
  const [keyLevels, setKeyLevels] = useState<KeyLevel[]>([{ id: 1, zone: '', type: '' }]);
  const [liqType, setLiqType] = useState<string | null>(null);
  const [clean, setClean] = useState<Clean>(null);
  const [sessions, setSessions] = useState<string[]>([]);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [trade_executer, setTradeExecuter] = useState<any>(null);
  const [showErrors, setShowErrors] = useState(false);
  const nextId = useRef(2);

  // Session store keys
  const ssPrefix = `market_${date}_${journal_data.id}_${account_number}_step2`;
  const ssKeys = {
    dir: `dir_${date}_${journal_data.id}_${account_number}_step2`,
    reason: `reason_${date}_${journal_data.id}_${account_number}_step2`,
    keyLevels: `keyLevels_${date}_${journal_data.id}_${account_number}_step2`,
    liqType: `liqType_${date}_${journal_data.id}_${account_number}_step2`,
    scenarioNote: `scenarioNote_${date}_${journal_data.id}_${account_number}_step2`,
    clean: `clean_${date}_${journal_data.id}_${account_number}_step2`,
    sessions: `sessions_${date}_${journal_data.id}_${account_number}_step2`,
  };

  const readSession = (key: string): string | null => {
    const raw = sessionStorage.getItem(key);
    if (raw === null) return null;
    try { return JSON.parse(raw); } catch { return raw; }
  };

  const removeSessionStore = () => {
    Object.values(ssKeys).forEach(k => sessionStorage.removeItem(k));
  };

  const isUsingSessionStore = Object.values(ssKeys).some(k => sessionStorage.getItem(k) !== null);

  // Dynamic Options với option.type chính xác
  const biasOptions = useMemo(() => getOptionsFromConfig(form_configs, 'pre_main_bias', [
    { label: 'BUY', value: 'BUY', type: 'success' },
    { label: 'SELL', value: 'SELL', type: 'danger' },
    { label: 'Đứng ngoài', value: 'NEUTRAL', type: 'neutral' }
  ], currentLanguage), [form_configs, currentLanguage]);

  const keyLevelTypeOptionsConfig = useMemo(() => {
    const opts = getOptionsFromConfig(form_configs, 'key_levels_type', [], currentLanguage);
    if (opts.length > 0) return opts;
    return (keyLevelTypeOptions || []).map((k: any) => typeof k === 'string' ? { label: k, value: k, type: 'neutral' } : { label: k.label || k.value, value: k.value || k.label, type: k.type || 'neutral' });
  }, [form_configs, currentLanguage]);

  const liqOptions = useMemo(() => getOptionsFromConfig(form_configs, 'pre_liquidity_zone', [
    { label: 'Quét rồi đảo chiều', value: 'Quét rồi đảo chiều', type: 'warning' },
    { label: 'Phá tiếp xu hướng', value: 'Phá tiếp xu hướng', type: 'success' },
    { label: 'Chưa rõ', value: 'Chưa rõ', type: 'neutral' }
  ], currentLanguage), [form_configs, currentLanguage]);

  const cleanOptions = useMemo(() => getOptionsFromConfig(form_configs, 'pre_market_condition', [
    { label: 'Vùng giá có giá trị buy', value: 'Vùng giá có giá trị buy', type: 'success' },
    { label: 'Vùng giá có giá trị sell', value: 'Vùng giá có giá trị sell', type: 'danger' }
  ], currentLanguage), [form_configs, currentLanguage]);

  const sessionOpts = useMemo(() => {
    const opts = getOptionsFromConfig(form_configs, 'pre_trading_session', [], currentLanguage);
    if (opts.length > 0) return opts;
    return (sessionOptions || []).map(s => ({ label: s.label, value: s.id, type: 'neutral' }));
  }, [form_configs, currentLanguage]);

  useEffect(() => {
    const matchingExecutions = journal_data?.trade_executions?.filter(e => e.symbol === symbol && e.account_number === account_number) || [];
    const execution = getMostCompleteExecution(matchingExecutions);
    // Start of day is one plan per day (saved without an account), but its market context used to reach only the
    // account selected when it was saved. Other accounts fall back to that day plan instead of starting empty.
    const jd: any = journal_data;
    let dayKeyLevels: any[] = [];
    try {
      const rawKL = jd?.key_level_zone ?? jd?.key_levels;
      dayKeyLevels = Array.isArray(rawKL) ? rawKL : (typeof rawKL === 'string' && rawKL.trim() ? JSON.parse(rawKL) : []);
    } catch { dayKeyLevels = []; }
    const ownPreTrade: any = execution?.pre_trade_data || {};
    const hasOwn = !!(ownPreTrade.key_levels?.length || ownPreTrade.pre_liquidity_zone || ownPreTrade.scenario_guess || ownPreTrade.pre_market_condition || ownPreTrade.pre_trading_session || ownPreTrade.pre_main_bias);
    const hasDayPlan = !!(dayKeyLevels.length || jd?.liquidity_status || jd?.scenario_guess || jd?.market_cleanliness || jd?.trading_session);
    setFromDayPlan(!hasOwn && hasDayPlan);
    const preTrade: any = {
      ...ownPreTrade,
      key_levels: ownPreTrade.key_levels?.length ? ownPreTrade.key_levels : dayKeyLevels,
      pre_liquidity_zone: ownPreTrade.pre_liquidity_zone ?? jd?.liquidity_status ?? null,
      scenario_guess: ownPreTrade.scenario_guess ?? jd?.scenario_guess ?? null,
      pre_market_condition: ownPreTrade.pre_market_condition ?? jd?.market_cleanliness ?? null,
      pre_trading_session: ownPreTrade.pre_trading_session ?? jd?.trading_session ?? null,
    };
    setTradeExecuter(execution || null);

    // dir: session > preTrade > null
    const sessionDir = readSession(ssKeys.dir);
    setDir(sessionDir ?? preTrade?.pre_main_bias ?? null);

    // reason: session > preTrade > ''
    const sessionReason = readSession(ssKeys.reason);
    setReason(sessionReason ?? preTrade?.bias_reason ?? '');

    // keyLevels: session > preTrade > default
    const sessionKL = sessionStorage.getItem(ssKeys.keyLevels);
    let klLoaded = false;
    if (sessionKL) {
      try {
        const parsed: KeyLevel[] = JSON.parse(sessionKL);
        if (Array.isArray(parsed) && parsed.length > 0) {
          setKeyLevels(parsed);
          nextId.current = Math.max(...parsed.map(k => k.id), 0) + 1;
          klLoaded = true;
        }
      } catch { /* ignore */ }
    }
    if (!klLoaded) {
      if (preTrade?.key_levels && preTrade.key_levels.length > 0) {
        const mappedLevels = preTrade.key_levels.map((k: any, idx: number) => ({
          id: idx + 1,
          zone: k.price_zone || '',
          type: k.zone_type || ''
        }));
        setKeyLevels(mappedLevels);
        nextId.current = mappedLevels.length + 1;
      } else {
        setKeyLevels([{ id: 1, zone: '', type: '' }]);
        nextId.current = 2;
      }
    }

    // liqType: session > preTrade > null
    const sessionLiq = readSession(ssKeys.liqType);
    setLiqType(sessionLiq ?? preTrade?.pre_liquidity_zone ?? null);

    // scenarioNote: session > preTrade > ''
    const sessionScenario = readSession(ssKeys.scenarioNote);
    setScenarioNote(sessionScenario ?? preTrade?.scenario_guess ?? '');

    // clean: session > preTrade > null
    const sessionClean = readSession(ssKeys.clean);
    setClean(sessionClean ?? preTrade?.pre_market_condition ?? null);

    // sessions: session > preTrade > []
    const sessionSessions = sessionStorage.getItem(ssKeys.sessions);
    if (sessionSessions) {
      try {
        const parsed = JSON.parse(sessionSessions);
        setSessions(Array.isArray(parsed) ? parsed : []);
      } catch { setSessions([]); }
    } else if (preTrade?.pre_trading_session) {
      const rawSessions = typeof preTrade.pre_trading_session === 'string'
        ? preTrade.pre_trading_session.split(',').map((s: string) => s.trim())
        : Array.isArray(preTrade.pre_trading_session) ? preTrade.pre_trading_session : [];
      setSessions(rawSessions);
    } else {
      setSessions([]);
    }
  }, [journal_data, account_number, symbol]);

  const toggleSession = (val: string) => {
    setSessions(s => {
      const next = s.includes(val) ? s.filter(x => x !== val) : [...s, val];
      sessionStorage.setItem(ssKeys.sessions, JSON.stringify(next));
      return next;
    });
  };

  const updateKL = (id: number, patch: Partial<KeyLevel>) => {
    setKeyLevels(ks => {
      const next = ks.map(k => k.id === id ? { ...k, ...patch } : k);
      sessionStorage.setItem(ssKeys.keyLevels, JSON.stringify(next));
      return next;
    });
  };

  const keyFilled = keyLevels.some(k => k.zone.trim() || k.type.trim());
  const score = [dir, reason.trim(), keyFilled, liqType, scenarioNote.trim(), clean, sessions.length > 0].filter(Boolean).length;
  const isCompleted = score >= 7;

  const validateAndHighlight = useCallback(() => {
    const keyFilled = keyLevels.some(k => k.zone.trim() || k.type.trim());
    const valid = !!(dir && reason.trim() && keyFilled && liqType && scenarioNote.trim() && clean && sessions.length > 0);
    if (!valid) {
      setShowErrors(true);
      return false;
    }
    return true;
  }, [dir, reason, keyLevels, liqType, scenarioNote, clean, sessions]);

  const reset = () => {
    setDir(null);
    setReason('');
    setScenarioNote('');
    setKeyLevels([{ id: 1, zone: '', type: '' }]);
    setLiqType(null);
    setClean(null);
    setSessions([]);
    nextId.current = 2;
    removeSessionStore();
    setShowErrors(false);
  };

  async function save() {
    if (!validateAndHighlight()) {
      message.warning('Vui lòng điền đầy đủ thông tin bối cảnh thị trường!');
      return false;
    }

    setSaveStatus('saving');
    try {
      const payloadPreTradeData = {
        pre_main_bias: dir,
        bias_reason: reason,
        key_levels: keyLevels.map(k => ({ price_zone: k.zone, zone_type: k.type })),
        pre_liquidity_zone: liqType,
        scenario_guess: scenarioNote,
        pre_market_condition: clean,
        pre_trading_session: sessions.length > 0 ? sessions.join(', ') : undefined
      };

      const data = await fetch(`${API_URL}/api/v1/be/checklist/trade`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify({
          ...(trade_executer?.id && { id: trade_executer.id }),
          date,
          journal_id: journal_data.id,
          account_number: account_number.replace("#", ""),
          symbol: symbol,
          status: "draft",
          pre_trade_data: payloadPreTradeData
        }),
      });

      if (!data.ok) {
        console.error('Failed to save pre-trade checklist', data.statusText);
        setSaveStatus('error');
        return false;
      } else {
        setSaveStatus('saved');
        removeSessionStore();
        setShowErrors(false);
        onSaveSuccess?.();
        return true;
      }
    } catch (error) {
      console.error('Error saving pre-trade checklist', error);
      setSaveStatus('error');
      return false;
    } finally {
      onReload?.();
      setTimeout(() => {
        setSaveStatus('idle');
      }, 2000);
    }
  }

  useEffect(() => {
    if (sectionRef) {
      sectionRef.current = {
        // Values pre-filled from the start-of-day plan are not stored for this account until saved
        isUnsaved: () => fromDayPlan || Object.values(ssKeys).some(k => sessionStorage.getItem(k) !== null),
        save: async () => {
          return await save();
        },
        isIncomplete: () => !isCompleted,
        validateAndHighlight: () => validateAndHighlight(),
      };
    }
    return () => {
      if (sectionRef) sectionRef.current = null;
    };
  }, [sectionRef, ssKeys, save, isCompleted, validateAndHighlight, fromDayPlan]);

  const chip = (
    <StatusChip tone={isUsingSessionStore || fromDayPlan ? 'gold' : isCompleted ? 'green' : 'gray'} dot>
      {isUsingSessionStore ? 'Chưa lưu' : fromDayPlan ? 'Lấy từ Đầu ngày · cần Lưu' : isCompleted ? 'Đã hoàn thành' : 'Chưa hoàn thành'}
    </StatusChip>
  );

  return (
    <CollapsibleSection num={1} title="Bối cảnh thị trường" open={open} onToggle={onToggle} chip={chip}>
      <div className="jd-two-col" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 28 }}>
        {/* LEFT */}
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <div style={{ fontSize: 13, fontWeight: 500, color: (showErrors && !dir) ? '#ef4444' : C.title, marginBottom: 9 }}>Hướng giao dịch chính</div>
          <div style={{
            display: 'flex', gap: 10, marginBottom: 16,
            ...(showErrors && !dir && { border: '1.5px solid #ef4444', borderRadius: 12, padding: '6px', boxShadow: '0 0 0 1px #ef4444' })
          }}>
            {biasOptions.map(opt => (
              <Pill
                key={opt.value}
                active={dir === opt.value}
                tone={getToneFromOptionType(opt.type)}
                onClick={() => {
                  setDir(opt.value);
                  sessionStorage.setItem(ssKeys.dir, JSON.stringify(opt.value));
                }}
              >
                {opt.label}
              </Pill>
            ))}
          </div>
          <Textarea
            value={reason}
            onChange={(v) => {
              setReason(v);
              sessionStorage.setItem(ssKeys.reason, JSON.stringify(v));
            }}
            placeholder="Lý do: cấu trúc H4 tăng, H1 tạo HH/HL…"
            hasError={showErrors && !reason.trim()}
          />

          <Divider />

          <div style={{ fontSize: 14, color: (showErrors && !keyLevels.some(k => k.zone.trim() || k.type.trim())) ? '#ef4444' : C.text, marginBottom: 11 }}>
            Giá đang tại <b style={{ fontWeight: 500 }}>Key Level quan trọng</b>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 9, marginBottom: 18 }}>
            {keyLevels.map(k => {
              const hasAnyKl = keyLevels.some(item => item.zone.trim() || item.type.trim());
              const zoneErr = Boolean(showErrors && (!hasAnyKl || (Boolean(k.type.trim()) && !k.zone.trim())));
              const typeErr = Boolean(showErrors && (!hasAnyKl || (Boolean(k.zone.trim()) && !k.type.trim())));
              return (
                <div key={k.id} style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
                  <ZoneInput value={k.zone} onChange={v => updateKL(k.id, { zone: v })} placeholder="Vùng giá: 2330 – 2335" hasError={zoneErr} />
                  <JdSelect
                    value={k.type}
                    onChange={v => updateKL(k.id, { type: v })}
                    items={keyLevelTypeOptionsConfig}
                    placeholder="Loại: Hỗ trợ / Kháng cự"
                    style={{
                      flex: 1,
                      ...(typeErr && { border: '1.5px solid #ef4444', borderRadius: 10, boxShadow: '0 0 0 1px #ef4444' })
                    }}
                  />
                  {keyLevels.length > 1 && (
                    <button onClick={() => {
                      setKeyLevels(ks => {
                        const next = ks.filter(x => x.id !== k.id);
                        sessionStorage.setItem(ssKeys.keyLevels, JSON.stringify(next));
                        return next;
                      });
                    }}
                      style={{ width: 38, height: 44, flex: 'none', borderRadius: 10, border: `1px solid ${C.iconBtnBd}`, background: C.surface, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: C.faint }}>
                      <Trash2 size={15} />
                    </button>
                  )}
                </div>
              );
            })}
            <button onClick={() => {
              const id = nextId.current++;
              setKeyLevels(ks => {
                const next = [...ks, { id, zone: '', type: '' }];
                sessionStorage.setItem(ssKeys.keyLevels, JSON.stringify(next));
                return next;
              });
            }}
              style={{ alignSelf: 'flex-start', display: 'inline-flex', alignItems: 'center', gap: 7, padding: '9px 14px', border: `1.5px dashed ${C.addDashedBd}`, borderRadius: 10, background: 'var(--jd-surface-2)', color: C.goldDark, fontFamily: 'inherit', fontSize: 13, fontWeight: 500, cursor: 'pointer' }}>
              <Plus size={15} /> Thêm mức giá
            </button>
          </div>

          <Divider style={{ margin: '0 0 18px' }} />

          <div style={{ fontSize: 14, color: (showErrors && !liqType) ? '#ef4444' : C.text, marginBottom: 12 }}>
            Đã xác định <b style={{ fontWeight: 500 }}>vùng thanh khoản / đỉnh đáy cần chú ý</b>
          </div>
          <div style={{
            display: 'flex', flexWrap: 'wrap', gap: 10,
            ...(showErrors && !liqType && { border: '1.5px solid #ef4444', borderRadius: 12, padding: '6px', boxShadow: '0 0 0 1px #ef4444' })
          }}>
            {liqOptions.map(opt => (
              <Pill key={opt.value} active={liqType === opt.value} tone={getToneFromOptionType(opt.type)} onClick={() => {
                setLiqType(opt.value);
                sessionStorage.setItem(ssKeys.liqType, JSON.stringify(opt.value));
              }}>
                {opt.label}
              </Pill>
            ))}
          </div>
        </div>

        {/* RIGHT */}
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <div style={{ fontSize: 14, color: (showErrors && !scenarioNote.trim()) ? '#ef4444' : C.text, marginBottom: 9 }}>
            Hiểu <b style={{ fontWeight: 500 }}>kịch bản có thể xảy ra tiếp theo</b>
          </div>
          <Textarea
            value={scenarioNote}
            onChange={(v) => {
              setScenarioNote(v);
              sessionStorage.setItem(ssKeys.scenarioNote, JSON.stringify(v));
            }}
            placeholder="Kịch bản A: giá hồi về Key Level rồi tăng tiếp. Kịch bản B: phá vùng → đảo chiều…"
            hasError={showErrors && !scenarioNote.trim()}
          />

          <Divider />

          <div style={{ fontSize: 13, fontWeight: 500, color: (showErrors && !clean) ? '#ef4444' : C.title, marginBottom: 9 }}>
            Thị trường đang ở giai đoạn nào ?
          </div>
          <div style={{
            display: 'flex', flexWrap: 'wrap', gap: 10, marginBottom: 18,
            ...(showErrors && !clean && { border: '1.5px solid #ef4444', borderRadius: 12, padding: '6px', boxShadow: '0 0 0 1px #ef4444' })
          }}>
            {cleanOptions.map((opt) => (
              <Pill key={opt.value} active={clean === opt.value} tone={getToneFromOptionType(opt.type)} onClick={() => {
                setClean(opt.value);
                sessionStorage.setItem(ssKeys.clean, JSON.stringify(opt.value));
              }}>
                {opt.label}
              </Pill>
            ))}
          </div>

          <Divider style={{ margin: '0 0 18px' }} />

          <div style={{ fontSize: 13, fontWeight: 500, color: (showErrors && sessions.length === 0) ? '#ef4444' : C.title, marginBottom: 9 }}>
            Phiên giao dịch hiện tại
          </div>
          <div style={{
            display: 'flex', flexWrap: 'wrap', gap: 10,
            ...(showErrors && sessions.length === 0 && { border: '1.5px solid #ef4444', borderRadius: 12, padding: '6px', boxShadow: '0 0 0 1px #ef4444' })
          }}>
            {sessionOpts.map(({ value, label, type }) => (
              <Pill key={value} active={sessions.includes(value)} tone={getToneFromOptionType(type)} onClick={() => toggleSession(value)}>
                {label}
              </Pill>
            ))}
          </div>
        </div>
      </div>
      {
        can_edit ? (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginTop: 20, paddingTop: 16, borderTop: `1px dashed ${C.border}` }}>
            <span style={{ fontSize: 12, color: C.faint }}></span>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <button
                onClick={reset}
                disabled={saveStatus === 'saving'}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 7, padding: '9px 15px',
                  border: `1px solid ${C.neutralBd}`, borderRadius: 10, background: C.surface,
                  color: C.muted, fontFamily: 'inherit', fontSize: 12.5, fontWeight: 500,
                  cursor: saveStatus === 'saving' ? 'not-allowed' : 'pointer',
                  opacity: saveStatus === 'saving' ? 0.6 : 1
                }}
              >
                <RotateCcw size={15} /> {t('Reset')}
              </button>
              <button
                onClick={save}
                disabled={saveStatus === 'saving' || saveStatus === 'saved'}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 7, padding: '9px 15px',
                  border: 'none', borderRadius: 10, fontFamily: 'inherit', fontSize: 12.5, fontWeight: 500,
                  transition: 'all 0.3s ease',
                  cursor: (saveStatus === 'idle' || saveStatus === 'error') ? 'pointer' : 'not-allowed',
                  background: saveStatus === 'saving'
                    ? C.neutralBd
                    : saveStatus === 'saved'
                      ? C.green
                      : saveStatus === 'error'
                        ? C.red
                        : C.blue,
                  color: C.white,
                }}
              >
                {saveStatus === 'saving' && (
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" className="jd-spin" style={{ animation: 'jd-keyframes-spin 1s linear infinite' }}>
                    <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                  </svg>
                )}
                {saveStatus === 'saved' && (
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M20 6L9 17l-5-5" />
                  </svg>
                )}
                {saveStatus === 'error' && (
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="18" y1="6" x2="6" y2="18"></line>
                    <line x1="6" y1="6" x2="18" y2="18"></line>
                  </svg>
                )}
                {saveStatus === 'idle' && <Save size={15} />}

                {saveStatus === 'saving' && t('Saving...')}
                {saveStatus === 'saved' && t('Saved!')}
                {saveStatus === 'error' && t('Error!')}
                {saveStatus === 'idle' && t('Save')}
              </button>
            </div>
          </div>
        )
          :
          (
            null
          )
      }
    </CollapsibleSection>
  );
}

function ZoneInput({ value, onChange, placeholder, hasError }: { value: string; onChange: (v: string) => void; placeholder: string; hasError?: boolean }) {
  return (
    <input value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder}
      style={{
        flex: 1, padding: '12px 14px',
        border: hasError ? '1.5px solid #ef4444' : `1px solid ${C.border}`,
        borderRadius: 11, fontSize: 14, color: C.text, outline: 'none', fontFamily: 'inherit', background: C.surface,
        ...(hasError && { boxShadow: '0 0 0 1px #ef4444' })
      }}
      onFocus={e => { e.currentTarget.style.borderColor = hasError ? '#ef4444' : C.gold; }}
      onBlur={e => { e.currentTarget.style.borderColor = hasError ? '#ef4444' : C.border; }} />
  );
}

function Textarea({ value, onChange, placeholder, style, hasError }: { value: string; onChange: (v: string) => void; placeholder: string; style?: React.CSSProperties; hasError?: boolean }) {
  return (
    <textarea value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder}
      style={{
        width: '100%', minHeight: 54, padding: '12px 14px',
        border: hasError ? '1.5px solid #ef4444' : `1px solid ${C.border}`,
        borderRadius: 11, fontSize: 14, color: C.text, lineHeight: 1.6, outline: 'none', fontFamily: 'inherit', resize: 'vertical',
        ...(hasError && { boxShadow: '0 0 0 1px #ef4444' }),
        ...style
      }}
      onFocus={e => { e.currentTarget.style.borderColor = hasError ? '#ef4444' : C.gold; }}
      onBlur={e => { e.currentTarget.style.borderColor = hasError ? '#ef4444' : C.border; }} />
  );
}

// Dynamic Option Group hiển thị theo Option.type ("success", "warning", "danger")
function DynamicOptionGroup({ label, options, selectedValue, onChange, currentLanguage }: {
  label: string; options: any[]; selectedValue: string | null; onChange: (val: string) => void; currentLanguage: string;
}) {
  return (
    <div>
      <div style={{ fontSize: 13.5, fontWeight: 600, color: C.title, marginBottom: 11 }}>{label}</div>
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        {options.map((opt, idx) => {
          const val = typeof opt === 'string' ? opt : (opt.value ?? opt.id ?? opt.label ?? '');
          const optLabel = typeof opt === 'string' ? opt : (currentLanguage === 'en' ? opt.label_en || opt.label_vi : opt.label_vi || opt.label_en || opt.label || val);
          const type = opt.type || opt.kind || (idx === 0 ? 'success' : 'danger');
          const isSelected = selectedValue === val;

          const colors = getOptionColorsByType(type, isSelected);

          return (
            <button
              key={val || idx}
              onClick={() => onChange(val)}
              style={{
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 9, padding: '11px 20px', borderRadius: 11,
                fontSize: 13.5, cursor: 'pointer', whiteSpace: 'nowrap', fontFamily: 'inherit',
                border: `1.5px solid ${isSelected ? colors.bd : C.chipOffBd}`, background: colors.bg,
                color: colors.tx, fontWeight: isSelected ? 700 : 600,
              }}
            >
              <span style={{
                width: 18, height: 18, flex: 'none', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: isSelected ? colors.bd : C.surface, border: isSelected ? 'none' : `1.6px solid ${C.checkOffBd}`
              }}>
                {isSelected && (
                  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12l5 5L20 6" /></svg>
                )}
              </span>
              {optLabel}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// Section 2 — Chất lượng Setup
interface SetupSectionProps {
  date: string;
  account_number: string;
  symbol: string;
  journal_data: Journal;
  form_configs: FormFieldConfig[];
  ui_labels: UILabels_PreTrade["step_2"];
  dynamic_checklist: DynamicChecklists;
  onReload?: () => void;
  open: boolean;
  can_edit?: boolean;
  onToggle: () => void;
  onSaveSuccess?: () => void;
  sectionRef?: React.MutableRefObject<SectionHandle | null>;
}

function SetupSection({ date, account_number, symbol, journal_data, form_configs, ui_labels, dynamic_checklist, onReload, open, can_edit, onToggle, onSaveSuccess, sectionRef }: SetupSectionProps) {
  const currentLanguage = i18next.language;

  const [checks, setChecks] = useState<Record<string, boolean>>({});
  const [preState, setPreState] = useState<string | null>(null);
  const [trade_executer, setTradeExecuter] = useState<any>(null);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [showErrors, setShowErrors] = useState(false);

  // Session store keys
  const ssKeys = {
    checks: `setup_checks_${date}_${journal_data.id}_${account_number}_step2`,
    preState: `setup_preState_${date}_${journal_data.id}_${account_number}_step2`,
  };

  const removeSessionStore = () => {
    Object.values(ssKeys).forEach(k => sessionStorage.removeItem(k));
  };

  const isUsingSessionStore = Object.values(ssKeys).some(k => sessionStorage.getItem(k) !== null);

  // Lấy options cho prior_trade_state với type chuẩn
  const preStateOptions = useMemo(() => {
    const opts = getOptionsFromConfig(form_configs, 'prior_trade_state', [], currentLanguage);
    if (opts.length > 0) {
      return opts.map(o => ({
        id: o.value,
        label: o.label,
        type: o.type || (o.value.toLowerCase().includes('win') || o.value.toLowerCase().includes('good') ? 'success' : 'danger')
      }));
    }
    return preTradeStates.map(p => ({ ...p, type: p.kind === 'good' ? 'success' : 'danger' }));
  }, [form_configs, currentLanguage]);

  const activeCriteria = useMemo(() => {
    const list = dynamic_checklist?.pre_trade || [];
    return list
      .filter(item => item.order_index >= 1 && item.order_index <= 3 && item.is_active === 1)
      .map(item => ({
        id: String(item.id),
        label: item.content
      }));
  }, [dynamic_checklist]);

  const checked = activeCriteria.filter(({ id }) => checks[id]).length;
  const isCompleted = checked > 0 && !!preState;

  const validateAndHighlight = useCallback(() => {
    const isComp = checked > 0 && !!preState;
    if (!isComp) {
      setShowErrors(true);
      return false;
    }
    return true;
  }, [checked, preState]);

  const reset = () => {
    setChecks({});
    setPreState(null);
    removeSessionStore();
    setShowErrors(false);
  };

  async function save() {
    if (!validateAndHighlight()) {
      message.warning('Vui lòng chọn tiêu chí setup và trạng thái trước lệnh!');
      return false;
    }

    const items = activeCriteria.map(({ id }) => ({
      item_id: Number(id),
      is_checked: !!checks[id]
    }));
    const prior_trade_state = preState;

    setSaveStatus('saving');
    try {
      const data = await fetch(`${API_URL}/api/v1/be/checklist/trade`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify({
          ...(trade_executer?.id && { id: trade_executer.id }),
          date,
          journal_id: journal_data.id,
          account_number: account_number.replace("#", ""),
          symbol: symbol,
          status: "draft",
          pre_trade_data: {
            prior_trade_state: prior_trade_state
          },
          items: items
        }),
      });
      if (!data.ok) {
        console.error('Failed to save pre-trade checklist', data.statusText);
        setSaveStatus('error');
        return false;
      } else {
        setSaveStatus('saved');
        removeSessionStore();
        setShowErrors(false);
        onSaveSuccess?.();
        return true;
      }
    } catch (error) {
      console.error('Error saving pre-trade checklist', error);
      setSaveStatus('error');
      return false;
    } finally {
      onReload?.();
      setTimeout(() => {
        setSaveStatus('idle');
      }, 2000);
    }
  }

  useEffect(() => {
    if (sectionRef) {
      sectionRef.current = {
        isUnsaved: () => Object.values(ssKeys).some(k => sessionStorage.getItem(k) !== null),
        save: async () => {
          return await save();
        },
        isIncomplete: () => {
          const c = activeCriteria.filter(({ id }) => checks[id]).length;
          return c === 0 || !preState;
        },
        validateAndHighlight: () => validateAndHighlight(),
      };
    }
    return () => {
      if (sectionRef) sectionRef.current = null;
    };
  }, [sectionRef, ssKeys, save, activeCriteria, checks, preState, validateAndHighlight]);

  useEffect(() => {
    const matchingExecutions = journal_data?.trade_executions?.filter(e => e.symbol === symbol && e.account_number === account_number) || [];
    const execution = getMostCompleteExecution(matchingExecutions, dynamic_checklist);
    setTradeExecuter(execution || null);

    // preState: session > API > null
    const sessionPreState = sessionStorage.getItem(ssKeys.preState);
    if (sessionPreState !== null) {
      try { setPreState(JSON.parse(sessionPreState)); } catch { setPreState(sessionPreState); }
    } else if (execution) {
      const apiPriorState = execution.pre_trade_data?.prior_trade_state;
      if (apiPriorState) {
        const matchedState = preStateOptions.find(
          (s: { id: string; label: string }) => s.label.toLowerCase() === apiPriorState.toLowerCase() || s.id === apiPriorState
        );
        setPreState(matchedState ? matchedState.id : apiPriorState);
      } else {
        setPreState(null);
      }
    } else {
      setPreState(null);
    }

    // checks: session > API > {}
    const sessionChecks = sessionStorage.getItem(ssKeys.checks);
    if (sessionChecks !== null) {
      try { setChecks(JSON.parse(sessionChecks)); } catch { setChecks({}); }
    } else if (execution) {
      const apiChecklist = execution.checklist_records || (execution as any).items || [];
      const newChecks: Record<string, boolean> = {};
      const apiCheckMap = new Map<number | string, boolean>();
      apiChecklist.forEach((item: any) => {
        const id = item.item_id || item.checklist_id || item.id;
        apiCheckMap.set(id, !!item.is_checked);
      });
      activeCriteria.forEach(({ id }) => {
        newChecks[id] = apiCheckMap.get(id) || apiCheckMap.get(Number(id)) || false;
      });
      setChecks(newChecks);
    } else {
      setChecks({});
    }
  }, [journal_data, account_number, symbol, preStateOptions, activeCriteria, dynamic_checklist]);

  const warn = preState ? (preStateWarnings as Record<string, string>)[preState] : null;

  const chip = (
    <StatusChip tone={isUsingSessionStore ? 'gold' : isCompleted ? 'green' : 'gray'} dot>
      {isUsingSessionStore ? 'Chưa lưu' : isCompleted ? 'Đã hoàn thành' : 'Chưa hoàn thành'}
    </StatusChip>
  );

  return (
    <CollapsibleSection num={2} title="Chất lượng Setup" open={open} onToggle={onToggle} chip={chip}>
      <div className="jd-two-col" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 28 }}>
        <div>
          <div style={{ fontSize: 13, fontWeight: 500, letterSpacing: 1, textTransform: 'uppercase', color: (showErrors && checked === 0) ? '#ef4444' : C.faint, marginBottom: 11 }}>Tiêu chí setup</div>
          <div style={{
            display: 'flex', flexDirection: 'column', gap: 3,
            ...(showErrors && checked === 0 && { border: '1.5px solid #ef4444', borderRadius: 12, padding: '6px', boxShadow: '0 0 0 1px #ef4444' })
          }}>
            {activeCriteria.map(({ id, label }) => {
              const done = !!checks[id];
              return (
                <div key={id} onClick={() => {
                  setChecks(c => {
                    const next = { ...c, [id]: !c[id] };
                    sessionStorage.setItem(ssKeys.checks, JSON.stringify(next));
                    return next;
                  });
                }}
                  style={{ display: 'flex', alignItems: 'center', gap: 13, padding: '11px 12px', borderRadius: 11, cursor: 'pointer', background: done ? 'var(--jd-done-bg)' : C.surface }}>
                  <CheckBox on={done} />
                  <span style={{ fontSize: 13.5, fontWeight: 500, lineHeight: 1.45, color: done ? C.onMuted : C.text }}>{label}</span>
                </div>
              );
            })}
          </div>
        </div>
        <div>
          <div style={{ fontSize: 13, fontWeight: 500, letterSpacing: 1, textTransform: 'uppercase', color: (showErrors && !preState) ? '#ef4444' : C.faint, marginBottom: 11 }}>Trạng thái trước lệnh này</div>
          <div style={{
            display: 'flex', flexWrap: 'wrap', gap: 10,
            ...(showErrors && !preState && { border: '1.5px solid #ef4444', borderRadius: 12, padding: '6px', boxShadow: '0 0 0 1px #ef4444' })
          }}>
            {preStateOptions.map(({ id, label, type }: { id: string; label: string; type?: string }) => (
              <Pill key={id} active={preState === id} tone={getToneFromOptionType(type)} onClick={() => {
                setPreState(id);
                sessionStorage.setItem(ssKeys.preState, JSON.stringify(id));
              }}>{label}</Pill>
            ))}
          </div>
          {warn && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 14, padding: '12px 14px', borderRadius: 11, background: C.warnBg, border: `1px solid ${C.warnBd}` }}>
              <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke={C.redTx} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ flex: 'none' }}><path d="M12 9v4M12 17h.01" /><path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z" /></svg>
              <span style={{ fontSize: 13, fontWeight: 600, color: C.redTx }}>{warn}</span>
            </div>
          )}
        </div>
      </div>
      {
        can_edit ? (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginTop: 20, paddingTop: 16, borderTop: `1px dashed ${C.border}` }}>
            <span style={{ fontSize: 12, color: C.faint }}></span>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <button
                onClick={reset}
                disabled={saveStatus === 'saving'}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 7, padding: '9px 15px',
                  border: `1px solid ${C.neutralBd}`, borderRadius: 10, background: C.surface,
                  color: C.muted, fontFamily: 'inherit', fontSize: 12.5, fontWeight: 500,
                  cursor: saveStatus === 'saving' ? 'not-allowed' : 'pointer',
                  opacity: saveStatus === 'saving' ? 0.6 : 1
                }}
              >
                <RotateCcw size={15} /> {t('Reset')}
              </button>
              <button
                onClick={save}
                disabled={saveStatus === 'saving' || saveStatus === 'saved'}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 7, padding: '9px 15px',
                  border: 'none', borderRadius: 10, fontFamily: 'inherit', fontSize: 12.5, fontWeight: 500,
                  transition: 'all 0.3s ease',
                  cursor: (saveStatus === 'idle' || saveStatus === 'error') ? 'pointer' : 'not-allowed',
                  background: saveStatus === 'saving'
                    ? C.neutralBd
                    : saveStatus === 'saved'
                      ? C.green
                      : saveStatus === 'error'
                        ? C.red
                        : C.blue,
                  color: C.white,
                }}
              >
                {saveStatus === 'saving' && (
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" className="jd-spin" style={{ animation: 'jd-keyframes-spin 1s linear infinite' }}>
                    <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                  </svg>
                )}
                {saveStatus === 'saved' && (
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M20 6L9 17l-5-5" />
                  </svg>
                )}
                {saveStatus === 'error' && (
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="18" y1="6" x2="6" y2="18"></line>
                    <line x1="6" y1="6" x2="18" y2="18"></line>
                  </svg>
                )}
                {saveStatus === 'idle' && <Save size={15} />}

                {saveStatus === 'saving' && t('Saving...')}
                {saveStatus === 'saved' && t('Saved!')}
                {saveStatus === 'error' && t('Error!')}
                {saveStatus === 'idle' && t('Save')}
              </button>
            </div>
          </div>
        )
          :
          (
            null
          )
      }
    </CollapsibleSection>
  );
}

function CheckBox({ on }: { on: boolean }) {
  return (
    <span style={{ width: 21, height: 21, flex: 'none', borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', background: on ? C.green : C.surface, border: on ? `1px solid ${C.green}` : `1.6px solid ${C.checkOffBd}` }}>
      {on && <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12l5 5L20 6" /></svg>}
    </span>
  );
}

// Section 3 — Quản trị rủi ro
interface RiskSectionProps {
  date: string;
  account_number: string;
  symbol: string;
  journal_data: Journal;
  form_configs: FormFieldConfig[];
  ui_labels: UILabels_PreTrade["step_3"];
  dynamic_checklist: DynamicChecklists;
  onReload?: () => void;
  open: boolean;
  can_edit?: boolean;
  onToggle: () => void;
  onSaveSuccess?: () => void;
  sectionRef?: React.MutableRefObject<SectionHandle | null>;
}

function RiskSection({ date, journal_data, account_number, symbol, form_configs, ui_labels, dynamic_checklist, onReload, open, can_edit, onToggle, onSaveSuccess, sectionRef }: RiskSectionProps) {
  const [checks, setChecks] = useState<Record<string, boolean>>({});
  const [trade_executer, setTradeExecuter] = useState<any>(null);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [showErrors, setShowErrors] = useState(false);

  // Session store keys
  const ssKeys = {
    checks: `risk_checks_${date}_${journal_data.id}_${account_number}_step3`,
  };

  const removeSessionStore = () => {
    Object.values(ssKeys).forEach(k => sessionStorage.removeItem(k));
  };

  const isUsingSessionStore = Object.values(ssKeys).some(k => sessionStorage.getItem(k) !== null);

  const activeRiskCriteria = useMemo(() => {
    const list = dynamic_checklist?.pre_trade || [];
    return list
      .filter(item => item.order_index > 3 && item.is_active === 1)
      .map(item => ({
        id: String(item.id),
        label: item.content,
        hint: item.weight_percent ? `Trọng số: ${item.weight_percent}%` : ''
      }));
  }, [dynamic_checklist]);

  const checked = activeRiskCriteria.filter(({ id }) => checks[id]).length;
  const isCompleted = checked > 0;

  const validateAndHighlight = useCallback(() => {
    if (checked === 0) {
      setShowErrors(true);
      return false;
    }
    return true;
  }, [checked]);

  useEffect(() => {
    const matchingExecutions = journal_data?.trade_executions?.filter(e => e.symbol === symbol && e.account_number === account_number) || [];
    const execution = getMostCompleteExecution(matchingExecutions, dynamic_checklist);
    setTradeExecuter(execution || null);

    // checks: session > API > {}
    const sessionChecks = sessionStorage.getItem(ssKeys.checks);
    if (sessionChecks !== null) {
      try { setChecks(JSON.parse(sessionChecks)); } catch { setChecks({}); }
    } else if (execution) {
      const apiChecklist = execution.checklist_records || (execution as any).items || [];
      const newChecks: Record<string, boolean> = {};
      const apiCheckMap = new Map<number | string, boolean>();
      apiChecklist.forEach((item: any) => {
        const id = item.item_id || item.checklist_id || item.id;
        apiCheckMap.set(id, !!item.is_checked);
      });
      activeRiskCriteria.forEach(({ id }) => {
        newChecks[id] = apiCheckMap.get(id) || apiCheckMap.get(Number(id)) || false;
      });
      setChecks(newChecks);
    } else {
      setChecks({});
    }
  }, [journal_data, activeRiskCriteria, account_number, symbol, dynamic_checklist]);

  const reset = () => {
    setChecks({});
    removeSessionStore();
    setShowErrors(false);
  };

  async function save() {
    if (!validateAndHighlight()) {
      message.warning('Vui lòng xác nhận ít nhất một tiêu chí quản trị rủi ro!');
      return false;
    }

    setSaveStatus('saving');
    try {
      const data = await fetch(`${API_URL}/api/v1/be/checklist/trade`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify({
          ...(trade_executer?.id && { id: trade_executer.id }),
          date,
          journal_id: journal_data.id,
          account_number: account_number.replace("#", ""),
          symbol: symbol,
          status: "draft",
          items: activeRiskCriteria.map(({ id }) => ({
            item_id: Number(id),
            is_checked: !!checks[id]
          }))
        }),
      });

      if (!data.ok) {
        console.error('Failed to save pre-trade checklist', data.statusText);
        setSaveStatus('error');
        return false;
      } else {
        setSaveStatus('saved');
        removeSessionStore();
        setShowErrors(false);
        onSaveSuccess?.();
        return true;
      }
    } catch (error) {
      console.error('Error saving pre-trade checklist', error);
      setSaveStatus('error');
      return false;
    } finally {
      onReload?.();
      setTimeout(() => {
        setSaveStatus('idle');
      }, 2000);
    }
  }

  useEffect(() => {
    if (sectionRef) {
      sectionRef.current = {
        isUnsaved: () => Object.values(ssKeys).some(k => sessionStorage.getItem(k) !== null),
        save: async () => {
          return await save();
        },
        isIncomplete: () => {
          const c = activeRiskCriteria.filter(({ id }) => checks[id]).length;
          return c === 0;
        },
        validateAndHighlight: () => validateAndHighlight(),
      };
    }
    return () => {
      if (sectionRef) sectionRef.current = null;
    };
  }, [sectionRef, ssKeys, save, activeRiskCriteria, checks, validateAndHighlight]);

  const chip = (
    <StatusChip tone={isUsingSessionStore ? 'gold' : isCompleted ? 'green' : 'gray'} dot>
      {isUsingSessionStore ? 'Chưa lưu' : isCompleted ? 'Đã hoàn thành' : 'Chưa hoàn thành'}
    </StatusChip>
  );

  return (
    <CollapsibleSection num={3} title="Quản trị rủi ro" open={open} onToggle={onToggle} chip={chip}>
      {/* 14 items one by one is slow when everything was checked: one tap to tick / clear all */}
      {can_edit && activeRiskCriteria.length > 0 && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 12 }}>
          <span style={{ fontSize: 13, fontWeight: 600, color: C.faint }}>Đã xác nhận {checked}/{activeRiskCriteria.length}</span>
          <button
            type="button"
            onClick={() => {
              const allOn = checked === activeRiskCriteria.length;
              const next: Record<string, boolean> = {};
              activeRiskCriteria.forEach(({ id }) => { next[id] = !allOn; });
              setChecks(next);
              sessionStorage.setItem(ssKeys.checks, JSON.stringify(next));
            }}
            style={{ padding: '6px 12px', borderRadius: 8, border: `1px solid ${C.neutralBd}`, background: C.surface, color: C.labelStrong, fontSize: 12.5, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}
          >
            {checked === activeRiskCriteria.length ? 'Bỏ chọn tất cả' : 'Chọn tất cả'}
          </button>
        </div>
      )}
      <div className="jd-two-col" style={{
        display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px 28px',
        ...(showErrors && checked === 0 && {
          border: '1.5px solid #ef4444',
          borderRadius: 12,
          padding: '10px',
          boxShadow: '0 0 0 1px #ef4444'
        })
      }}>
        {activeRiskCriteria.map(({ id, label: title }) => {
          const done = !!checks[id];
          return (
            <div key={id} onClick={() => {
              setChecks(c => {
                const next = { ...c, [id]: !c[id] };
                sessionStorage.setItem(ssKeys.checks, JSON.stringify(next));
                return next;
              });
            }}
              style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '11px 12px', borderRadius: 11, cursor: 'pointer', background: done ? 'var(--jd-done-bg)' : C.surface }}>
              <span style={{ marginTop: 1 }}><CheckBox on={done} /></span>
              <span style={{ flex: 1, fontSize: 13.5, fontWeight: 500, lineHeight: 1.4, color: done ? C.onMuted : C.text }}>{title}</span>
            </div>
          );
        })}
      </div>
      {
        can_edit ? (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginTop: 20, paddingTop: 16, borderTop: `1px dashed ${C.border}` }}>
            <span style={{ fontSize: 12, color: C.faint }}></span>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <button
                onClick={reset}
                disabled={saveStatus === 'saving'}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 7, padding: '9px 15px',
                  border: `1px solid ${C.neutralBd}`, borderRadius: 10, background: C.surface,
                  color: C.muted, fontFamily: 'inherit', fontSize: 12.5, fontWeight: 500,
                  cursor: saveStatus === 'saving' ? 'not-allowed' : 'pointer',
                  opacity: saveStatus === 'saving' ? 0.6 : 1
                }}
              >
                <RotateCcw size={15} /> {t('Reset')}
              </button>
              <button
                onClick={save}
                disabled={saveStatus === 'saving' || saveStatus === 'saved'}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 7, padding: '9px 15px',
                  border: 'none', borderRadius: 10, fontFamily: 'inherit', fontSize: 12.5, fontWeight: 500,
                  transition: 'all 0.3s ease',
                  cursor: (saveStatus === 'idle' || saveStatus === 'error') ? 'pointer' : 'not-allowed',
                  background: saveStatus === 'saving'
                    ? C.neutralBd
                    : saveStatus === 'saved'
                      ? C.green
                      : saveStatus === 'error'
                        ? C.red
                        : C.blue,
                  color: C.white,
                }}
              >
                {saveStatus === 'saving' && (
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" className="jd-spin" style={{ animation: 'jd-keyframes-spin 1s linear infinite' }}>
                    <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                  </svg>
                )}
                {saveStatus === 'saved' && (
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M20 6L9 17l-5-5" />
                  </svg>
                )}
                {saveStatus === 'error' && (
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="18" y1="6" x2="6" y2="18"></line>
                    <line x1="6" y1="6" x2="18" y2="18"></line>
                  </svg>
                )}
                {saveStatus === 'idle' && <Save size={15} />}

                {saveStatus === 'saving' && t('Saving...')}
                {saveStatus === 'saved' && t('Saved!')}
                {saveStatus === 'error' && t('Error!')}
                {saveStatus === 'idle' && t('Save')}
              </button>
            </div>
          </div>
        )
          :
          (
            null
          )
      }
    </CollapsibleSection>
  );
}

// Section 4 — Tâm lý giao dịch
const MOOD_TONE = {
  pos: { bd: C.greenBd, bg: C.greenBg, tx: C.greenTx },
  neg: { bd: C.goldBd, bg: C.goldBg, tx: C.goldDark },
  danger: { bd: C.redBd, bg: C.redBg, tx: C.redTx },
};
interface TonedMood { id: string; label: string; tone: MoodTone }
const ALL_MOODS: TonedMood[] = [
  ...moodPositive.map(({ id, label }) => ({ id, label, tone: 'pos' as MoodTone })),
  ...moodNegative.map(({ id, label }) => ({ id, label, tone: 'neg' as MoodTone })),
  ...moodDanger.map(({ id, label }) => ({ id, label, tone: 'danger' as MoodTone })),
];

interface PsychologySectionProps {
  date: string;
  account_number: string;
  symbol: string;
  journal_data: Journal;
  form_configs: FormFieldConfig[];
  ui_labels: UILabels_PreTrade["step_4"];
  dynamic_checklist: DynamicChecklists;
  onReload?: () => void;
  open: boolean;
  can_edit?: boolean;
  onToggle: () => void;
  onSaveSuccess?: () => void;
  sectionRef?: React.MutableRefObject<SectionHandle | null>;
}

function PsychologySection({ date, journal_data, account_number, symbol, form_configs, ui_labels, dynamic_checklist, onReload, open, onToggle, onSaveSuccess, can_edit, sectionRef }: PsychologySectionProps) {
  const currentLanguage = i18next.language;

  const [mode, setMode] = useState<'quick' | 'full'>('quick');
  const [moods, setMoods] = useState<string[]>([]);
  const [wcSL, setWcSL] = useState('');
  const [wcDD, setWcDD] = useState('');
  const [accept, setAccept] = useState<string | null>(null);
  const [trade_executer, setTradeExecuter] = useState<any>(null);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [showErrors, setShowErrors] = useState(false);

  // Session store keys
  const ssKeys = {
    moods: `psych_moods_${date}_${journal_data.id}_${account_number}_step4`,
    wcSL: `psych_wcSL_${date}_${journal_data.id}_${account_number}_step4`,
    wcDD: `psych_wcDD_${date}_${journal_data.id}_${account_number}_step4`,
    accept: `psych_accept_${date}_${journal_data.id}_${account_number}_step4`,
  };

  const readSession = (key: string): string | null => {
    const raw = sessionStorage.getItem(key);
    if (raw === null) return null;
    try { return JSON.parse(raw); } catch { return raw; }
  };

  const removeSessionStore = () => {
    Object.values(ssKeys).forEach(k => sessionStorage.removeItem(k));
  };

  const isUsingSessionStore = Object.values(ssKeys).some(k => sessionStorage.getItem(k) !== null);

  // Dynamic Options cho Risk Acceptance với type màu tương ứng
  const riskAcceptanceOptions = useMemo(() => getOptionsFromConfig(form_configs, 'risk_acceptance', [
    { label: '✅ Có, đây là khoản lỗ nằm trong kế hoạch', value: 'accept', type: 'success' },
    { label: '🤔 Chưa hoàn toàn thoải mái', value: 'uncomfortable', type: 'warning' },
    { label: '❌ Không, khoản lỗ này quá lớn', value: 'reject', type: 'danger' }
  ], currentLanguage), [form_configs, currentLanguage]);

  useEffect(() => {
    const matchingExecutions = journal_data?.trade_executions?.filter(e => e.symbol === symbol && e.account_number === account_number) || [];
    const execution = getMostCompleteExecution(matchingExecutions, dynamic_checklist);
    const preTrade = execution?.pre_trade_data;
    setTradeExecuter(execution || null);

    // moods: session > preTrade > []
    const sessionMoods = sessionStorage.getItem(ssKeys.moods);
    if (sessionMoods !== null) {
      try { setMoods(JSON.parse(sessionMoods)); } catch { setMoods([]); }
    } else if (preTrade) {
      const apiEmotions = preTrade.pre_emotions as unknown;
      if (Array.isArray(apiEmotions)) {
        setMoods(apiEmotions.map((m: any) => String(m)));
      } else if (typeof apiEmotions === 'string' && apiEmotions.trim() !== '') {
        setMoods(apiEmotions.split(',').map((m: string) => m.trim()));
      } else {
        setMoods([]);
      }
    } else {
      setMoods([]);
    }

    // wcSL: session > preTrade > ''
    const sessionSL = readSession(ssKeys.wcSL);
    if (sessionSL !== null) {
      setWcSL(sessionSL);
    } else {
      // Fall back to the start-of-day risk per trade, so the user only confirms it instead of typing it again
      const dayRisk = Number((journal_data as any)?.max_risk_per_trade) || 0;
      setWcSL(preTrade?.sl_loss_percent !== undefined && preTrade?.sl_loss_percent !== null ? String(preTrade.sl_loss_percent) : dayRisk > 0 ? String(dayRisk) : '');
    }

    // wcDD: session > preTrade > ''
    const sessionDD = readSession(ssKeys.wcDD);
    if (sessionDD !== null) {
      setWcDD(sessionDD);
    } else {
      // Same fallback: drawdown after 3 losses in a row = 3 x the planned risk per trade
      const dayRisk = Number((journal_data as any)?.max_risk_per_trade) || 0;
      setWcDD(preTrade?.dd_left_percent !== undefined && preTrade?.dd_left_percent !== null ? String(preTrade.dd_left_percent) : dayRisk > 0 ? String(Math.round(dayRisk * 3 * 100) / 100) : '');
    }

    // accept: session > preTrade > null
    const sessionAccept = readSession(ssKeys.accept);
    setAccept(sessionAccept ?? preTrade?.risk_acceptance ?? null);
  }, [journal_data, account_number, symbol, dynamic_checklist]);

  const toggleMood = (id: string) => {
    setMoods(ms => {
      const next = ms.includes(id) ? ms.filter(x => x !== id) : [...ms, id];
      sessionStorage.setItem(ssKeys.moods, JSON.stringify(next));
      return next;
    });
  };
  const dangerWarn = moodDanger.some(({ id }) => moods.includes(id));
  const picked = moods.length;
  const isCompleted = picked > 0 && wcSL && wcDD && accept;

  const validateAndHighlight = useCallback(() => {
    const isComp = moods.length > 0 && !!wcSL && !!wcDD && !!accept;
    if (!isComp) {
      setShowErrors(true);
      return false;
    }
    return true;
  }, [moods, wcSL, wcDD, accept]);

  const wcNoteMap: Record<string, { box: { bg: string; bd: string; tx: string }; text: string }> = {
    accept: { box: { bg: C.greenBg, bd: C.greenBd, tx: C.greenTx }, text: 'Tốt — bạn đã chấp nhận khoản lỗ tối đa. Tâm thế sẵn sàng vào lệnh theo kế hoạch.' },
    uncomfortable: { box: { bg: C.goldBg, bd: C.goldBd, tx: C.goldDark }, text: 'Chưa hoàn toàn thoải mái — cân nhắc giảm khối lượng để mức lỗ về ngưỡng bạn thật sự chấp nhận được.' },
    reject: { box: { bg: C.warnBg, bd: C.warnBd, tx: C.redTx }, text: '"Nếu bạn không sẵn sàng chấp nhận khoản lỗ này ngay lúc này, bạn chưa sẵn sàng vào lệnh." → Nên đứng ngoài.' },
  };
  const wcNote = accept ? (wcNoteMap[accept.toLowerCase()] || null) : null;

  const reset = () => {
    setMoods([]);
    setWcSL('');
    setWcDD('');
    setAccept(null);
    removeSessionStore();
    setShowErrors(false);
  };

  async function save() {
    if (!validateAndHighlight()) {
      message.warning('Vui lòng hoàn thành đầy đủ thông tin tâm lý giao dịch!');
      return false;
    }

    setSaveStatus('saving');
    try {
      const data = await fetch(`${API_URL}/api/v1/be/checklist/trade`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify({
          ...(trade_executer?.id && { id: trade_executer.id }),
          date,
          journal_id: journal_data.id,
          account_number: account_number.replace("#", ""),
          symbol: symbol,
          status: "draft",
          pre_trade_data: {
            sl_loss_percent: wcSL ? Number(wcSL) : null,
            dd_left_percent: wcDD ? Number(wcDD) : null,
            risk_acceptance: accept,
            pre_emotions: moods,
          }
        }),
      });
      if (!data.ok) {
        console.error('Failed to save pre-trade checklist', data.statusText);
        setSaveStatus('error');
        return false;
      } else {
        setSaveStatus('saved');
        removeSessionStore();
        setShowErrors(false);
        onSaveSuccess?.();
        return true;
      }
    } catch (error) {
      console.error('Error saving pre-trade checklist', error);
      setSaveStatus('error');
      return false;
    } finally {
      onReload?.();
      setTimeout(() => {
        setSaveStatus('idle');
      }, 2000);
    }
  }

  useEffect(() => {
    if (sectionRef) {
      sectionRef.current = {
        isUnsaved: () => Object.values(ssKeys).some(k => sessionStorage.getItem(k) !== null),
        save: async () => {
          return await save();
        },
        isIncomplete: () => {
          return !(moods.length > 0 && !!wcSL && !!wcDD && !!accept);
        },
        validateAndHighlight: () => validateAndHighlight(),
      };
    }
    return () => {
      if (sectionRef) sectionRef.current = null;
    };
  }, [sectionRef, ssKeys, save, moods, wcSL, wcDD, accept, validateAndHighlight]);

  const moodChip = (id: string, label: string, tone: MoodTone) => {
    const on = moods.includes(id);
    const c = MOOD_TONE[tone];
    return (
      <button key={id} onClick={() => toggleMood(id)}
        style={{ fontSize: 13, fontWeight: 600, padding: '8px 14px', borderRadius: 999, cursor: 'pointer', transition: 'all .15s', whiteSpace: 'nowrap', border: `1.5px solid ${on ? c.bd : C.chipOffBd}`, background: on ? c.bg : C.surface, color: on ? c.tx : C.chipOffTx, fontFamily: 'inherit' }}>
        {label}
      </button>
    );
  };

  const seg = (active: boolean) => ({
    padding: '6px 14px', borderRadius: 7, border: 'none', fontFamily: 'inherit', fontSize: 13, fontWeight: 500, cursor: 'pointer',
    background: active ? C.surface : 'transparent', color: active ? C.title : C.onMuted, boxShadow: active ? '0 1px 3px rgba(17,26,39,0.12)' : 'none',
  } as React.CSSProperties);

  const chip = (
    <StatusChip dot tone={isUsingSessionStore ? 'gold' : isCompleted ? 'green' : 'gray'}>
      {isUsingSessionStore ? 'Chưa lưu' : isCompleted ? 'Đã hoàn thành' : 'Chưa hoàn thành'}
    </StatusChip>
  );

  return (
    <CollapsibleSection num={4} title="Tâm lý giao dịch" open={open} onToggle={onToggle} chip={chip}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 14, marginBottom: 16 }}>
        <span style={{ fontSize: 14, fontWeight: 500, color: (showErrors && moods.length === 0) ? '#ef4444' : C.title }}>Cảm xúc thực sự lúc này</span>
        <div style={{ display: 'flex', background: 'var(--jd-surface-4)', borderRadius: 9, padding: 3 }}>
          <button onClick={() => setMode('quick')} style={seg(mode === 'quick')}>Nhanh</button>
          <button onClick={() => setMode('full')} style={seg(mode === 'full')}>Đầy đủ</button>
        </div>
      </div>

      {picked > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8, padding: '12px 14px', marginBottom: 18, borderRadius: 11, background: 'var(--jd-surface-3)', border: `1px solid ${C.divider}` }}>
          <span style={{ fontSize: 13, fontWeight: 600, color: C.faint, marginRight: 2 }}>Đã chọn:</span>
          {moods.map(id => {
            const found = ALL_MOODS.find(m => m.id === id);
            const tone = (found?.tone ?? 'neg');
            const c = MOOD_TONE[tone];
            return (
              <button key={id} onClick={() => toggleMood(id)} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 600, padding: '5px 11px', borderRadius: 999, cursor: 'pointer', border: `1.5px solid ${c.bd}`, background: c.bg, color: c.tx, fontFamily: 'inherit' }}>
                {found?.label ?? id}<span style={{ fontSize: 14, lineHeight: 1, opacity: .6 }}>×</span>
              </button>
            );
          })}
          <button onClick={() => setMoods([])} style={{ marginLeft: 'auto', fontSize: 13, fontWeight: 600, color: C.faint, background: 'none', border: 'none', cursor: 'pointer', padding: '4px 6px', fontFamily: 'inherit' }}>Xoá hết</button>
        </div>
      )}

      {mode === 'quick' ? (
        <div style={{
          display: 'flex', flexWrap: 'wrap', gap: 9,
          ...(showErrors && moods.length === 0 && { border: '1.5px solid #ef4444', borderRadius: 12, padding: '8px', boxShadow: '0 0 0 1px #ef4444' })
        }}>
          {moodQuick.map(({ id, label, tone }) => moodChip(id, label, tone as MoodTone))}
        </div>
      ) : (
        <div style={{
          ...(showErrors && moods.length === 0 && { border: '1.5px solid #ef4444', borderRadius: 12, padding: '8px', boxShadow: '0 0 0 1px #ef4444' })
        }}>
          <MoodGroupLabel dot={C.green} tx={C.greenTx}>Cảm xúc tích cực</MoodGroupLabel>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 9, marginBottom: 20 }}>{moodPositive.map(({ id, label }) => moodChip(id, label, 'pos'))}</div>
          <MoodGroupLabel dot={C.gold} tx={C.goldDark}>Cảm xúc tiêu cực</MoodGroupLabel>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 9, marginBottom: 20 }}>{moodNegative.map(({ id, label }) => moodChip(id, label, 'neg'))}</div>
          <MoodGroupLabel dot={C.red} tx={C.redTx}>Trạng thái nguy hiểm khi giao dịch</MoodGroupLabel>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 9 }}>{moodDanger.map(({ id, label }) => moodChip(id, label, 'danger'))}</div>
        </div>
      )}

      {dangerWarn && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 16, padding: '12px 14px', borderRadius: 11, background: C.warnBg, border: `1px solid ${C.warnBd}` }}>
          <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke={C.redTx} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ flex: 'none' }}><path d="M12 9v4M12 17h.01" /><path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z" /></svg>
          <span style={{ fontSize: 13, fontWeight: 600, color: C.redTx }}>Bạn đang ở trạng thái tâm lý nguy hiểm — cân nhắc đứng ngoài hoặc giảm khối lượng mạnh.</span>
        </div>
      )}

      <Divider style={{ margin: '22px 0' }} />

      {/* Worst-case acceptance */}
      <div style={{ background: 'var(--jd-surface-3)', border: `1px solid ${C.borderSoft}`, borderRadius: 13, padding: 18 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
          <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke={C.gold} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 9v4M12 17h.01" /><path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z" /></svg>
          <span style={{ fontSize: 14, fontWeight: 500, color: C.title }}>Kịch bản xấu nhất đã được chấp nhận?</span>
        </div>
        <div className="jd-two-col" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
          <div>
            <div style={{ fontSize: 13, fontWeight: 600, color: (showErrors && !wcSL) ? '#ef4444' : C.labelStrong, marginBottom: 7 }}>Nếu hit SL tôi mất (% / lệnh)</div>
            <WcInput value={wcSL} onChange={(v) => {
              setWcSL(v);
              sessionStorage.setItem(ssKeys.wcSL, JSON.stringify(v));
            }} placeholder="VD: 1.0" hasError={showErrors && !wcSL} />
          </div>
          <div>
            <div style={{ fontSize: 13, fontWeight: 600, color: (showErrors && !wcDD) ? '#ef4444' : C.labelStrong, marginBottom: 7 }}>Nếu thua 3 lệnh liên tiếp, DD còn (%)</div>
            <WcInput value={wcDD} onChange={(v) => {
              setWcDD(v);
              sessionStorage.setItem(ssKeys.wcDD, JSON.stringify(v));
            }} placeholder="VD: 3.0" hasError={showErrors && !wcDD} />
          </div>
        </div>
        <div style={{ fontSize: 13, fontWeight: 500, color: (showErrors && !accept) ? '#ef4444' : C.title, marginBottom: 10 }}>Tôi chấp nhận điều đó không?</div>
        <div style={{
          display: 'flex', flexWrap: 'wrap', gap: 10,
          ...(showErrors && !accept && { border: '1.5px solid #ef4444', borderRadius: 12, padding: '6px', boxShadow: '0 0 0 1px #ef4444' })
        }}>
          {riskAcceptanceOptions.map(opt => (
            <Pill key={opt.value} active={accept === opt.value} tone={getToneFromOptionType(opt.type)} onClick={() => {
              setAccept(opt.value);
              sessionStorage.setItem(ssKeys.accept, JSON.stringify(opt.value));
            }}>
              {opt.label}
            </Pill>
          ))}
        </div>
        {wcNote && (
          <div style={{ marginTop: 16, padding: '12px 14px', borderRadius: 11, background: wcNote.box.bg, border: `1px solid ${wcNote.box.bd}`, color: wcNote.box.tx, fontSize: 13, fontWeight: 500, lineHeight: 1.55 }}>{wcNote.text}</div>
        )}
      </div>
      {
        can_edit ? (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginTop: 20, paddingTop: 16, borderTop: `1px dashed ${C.border}` }}>
            <span style={{ fontSize: 12, color: C.faint }}></span>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <button
                onClick={reset}
                disabled={saveStatus === 'saving'}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 7, padding: '9px 15px',
                  border: `1px solid ${C.neutralBd}`, borderRadius: 10, background: C.surface,
                  color: C.muted, fontFamily: 'inherit', fontSize: 12.5, fontWeight: 500,
                  cursor: saveStatus === 'saving' ? 'not-allowed' : 'pointer',
                  opacity: saveStatus === 'saving' ? 0.6 : 1
                }}
              >
                <RotateCcw size={15} /> {t('Reset')}
              </button>
              <button
                onClick={save}
                disabled={saveStatus === 'saving' || saveStatus === 'saved'}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 7, padding: '9px 15px',
                  border: 'none', borderRadius: 10, fontFamily: 'inherit', fontSize: 12.5, fontWeight: 500,
                  transition: 'all 0.3s ease',
                  cursor: (saveStatus === 'idle' || saveStatus === 'error') ? 'pointer' : 'not-allowed',
                  background: saveStatus === 'saving'
                    ? C.neutralBd
                    : saveStatus === 'saved'
                      ? C.green
                      : saveStatus === 'error'
                        ? C.red
                        : C.blue,
                  color: C.white,
                }}
              >
                {saveStatus === 'saving' && (
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" className="jd-spin" style={{ animation: 'jd-keyframes-spin 1s linear infinite' }}>
                    <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                  </svg>
                )}
                {saveStatus === 'saved' && (
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M20 6L9 17l-5-5" />
                  </svg>
                )}
                {saveStatus === 'error' && (
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="18" y1="6" x2="6" y2="18"></line>
                    <line x1="6" y1="6" x2="18" y2="18"></line>
                  </svg>
                )}
                {saveStatus === 'idle' && <Save size={15} />}

                {saveStatus === 'saving' && t('Saving...')}
                {saveStatus === 'saved' && t('Saved!')}
                {saveStatus === 'error' && t('Error!')}
                {saveStatus === 'idle' && t('Save')}
              </button>
            </div>
          </div>
        )
          : (
            null
          )
      }
    </CollapsibleSection>
  );
}

function MoodGroupLabel({ dot, tx, children }: { dot: string; tx: string; children: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
      <span style={{ width: 8, height: 8, borderRadius: '50%', background: dot }} />
      <span style={{ fontSize: 13, fontWeight: 500, letterSpacing: .5, textTransform: 'uppercase', color: tx }}>{children}</span>
    </div>
  );
}

function WcInput({ value, onChange, placeholder, hasError }: { value: string; onChange: (v: string) => void; placeholder: string; hasError?: boolean }) {
  return (
    <input value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder}
      style={{
        width: '100%', padding: '12px 14px',
        border: hasError ? '1.5px solid #ef4444' : `1px solid ${C.border}`,
        borderRadius: 11, fontSize: 14, fontWeight: 500, color: C.text, outline: 'none', background: C.surface, fontFamily: 'inherit',
        ...(hasError && { boxShadow: '0 0 0 1px #ef4444' })
      }}
      onFocus={e => { e.currentTarget.style.borderColor = hasError ? '#ef4444' : C.gold; }}
      onBlur={e => { e.currentTarget.style.borderColor = hasError ? '#ef4444' : C.border; }} />
  );
}

interface StepBeforeEntryProps {
  date: string;
  account_number: string;
  symbol: string;
  journal_data: Journal;
  dynamic_checklist: DynamicChecklists;
  form_configs: FormFieldConfig[];
  ui_labels: UILabels_PreTrade;
  can_edit?: boolean;
  onReload?: () => void;
}

const StepBeforeEntry = forwardRef<StepRefHandle, StepBeforeEntryProps>(function StepBeforeEntry(
  { date, account_number, symbol, journal_data, dynamic_checklist, form_configs, ui_labels, can_edit, onReload }: StepBeforeEntryProps,
  ref
) {
  const matchingExecutions = journal_data?.trade_executions?.filter(e => e.symbol === symbol && e.account_number === account_number) || [];
  const execution = getMostCompleteExecution(matchingExecutions, dynamic_checklist);

  const [activeSection, setActiveSection] = useState<number | null>(1);

  const marketSectionRef = useRef<SectionHandle | null>(null);
  const setupSectionRef = useRef<SectionHandle | null>(null);
  const riskSectionRef = useRef<SectionHandle | null>(null);
  const psychSectionRef = useRef<SectionHandle | null>(null);

  useImperativeHandle(ref, () => ({
    hasUnsavedChanges: () => {
      const allRefs = [
        marketSectionRef.current,
        setupSectionRef.current,
        riskSectionRef.current,
        psychSectionRef.current,
      ];
      return allRefs.some(sec => sec?.isUnsaved());
    },
    saveChanges: async () => {
      const allRefs = [
        marketSectionRef.current,
        setupSectionRef.current,
        riskSectionRef.current,
        psychSectionRef.current,
      ];
      let allSuccess = true;
      for (const sec of allRefs) {
        if (sec?.isUnsaved()) {
          // Bỏ qua các section chưa hoàn thành vì không thể gửi dữ liệu thiếu lên API
          if (sec.isIncomplete?.()) {
            continue;
          }
          const res = await sec.save();
          if (res === false) {
            allSuccess = false;
          }
        }
      }
      return allSuccess;
    },
    getIncompleteSections: () => {
      const missing: IncompleteSection[] = [];
      if (marketSectionRef.current?.isIncomplete?.()) {
        missing.push({ num: 1, title: 'Bối cảnh thị trường' });
      }
      if (setupSectionRef.current?.isIncomplete?.()) {
        missing.push({ num: 2, title: 'Chất lượng Setup' });
      }
      if (riskSectionRef.current?.isIncomplete?.()) {
        missing.push({ num: 3, title: 'Quản trị rủi ro' });
      }
      if (psychSectionRef.current?.isIncomplete?.()) {
        missing.push({ num: 4, title: 'Tâm lý giao dịch' });
      }
      return missing;
    },
    validateAndHighlight: () => {
      let isValid = true;
      let firstIncomplete: number | null = null;

      if (marketSectionRef.current?.isIncomplete?.()) {
        marketSectionRef.current?.validateAndHighlight?.();
        if (firstIncomplete === null) firstIncomplete = 1;
        isValid = false;
      }
      if (setupSectionRef.current?.isIncomplete?.()) {
        setupSectionRef.current?.validateAndHighlight?.();
        if (firstIncomplete === null) firstIncomplete = 2;
        isValid = false;
      }
      if (riskSectionRef.current?.isIncomplete?.()) {
        riskSectionRef.current?.validateAndHighlight?.();
        if (firstIncomplete === null) firstIncomplete = 3;
        isValid = false;
      }
      if (psychSectionRef.current?.isIncomplete?.()) {
        psychSectionRef.current?.validateAndHighlight?.();
        if (firstIncomplete === null) firstIncomplete = 4;
        isValid = false;
      }

      if (firstIncomplete !== null) {
        setActiveSection(firstIncomplete);
      }
      return isValid;
    }
  }));

  const getSectionRef = (num: number) => {
    switch (num) {
      case 1: return marketSectionRef.current;
      case 2: return setupSectionRef.current;
      case 3: return riskSectionRef.current;
      case 4: return psychSectionRef.current;
      default: return null;
    }
  };

  const isSwitchingRef = useRef(false);

  const handleToggle = useCallback(async (num: number) => {
    if (isSwitchingRef.current) return;

    if (activeSection !== null) {
      const currentRef = getSectionRef(activeSection);
      if (currentRef) {
        const isIncomplete = currentRef.isIncomplete?.();
        // Chỉ cảnh báo khi người dùng bấm tiến lên section phía sau (num > activeSection)
        // Khi quay lại section trước (num < activeSection) hoặc đóng, luôn cho phép chuyển
        if (activeSection !== num && num > activeSection) {
          if (isIncomplete) {
            currentRef.validateAndHighlight?.();
            message.warning('Vui lòng hoàn thành các thông tin còn thiếu ở phần hiện tại trước khi chuyển!');
            return;
          }
        }

        // Locked days (older than 5 days / future) have no Save button, so never auto-save them either
        if (can_edit && !isIncomplete && (!currentRef.isUnsaved || currentRef.isUnsaved())) {
          try {
            isSwitchingRef.current = true;
            const res = await currentRef.save();
            if (res === false && activeSection !== num && num > activeSection) {
              return;
            }
          } catch (e) {
            console.error("Auto save error on section change:", e);
            if (activeSection !== num && num > activeSection) return;
          } finally {
            isSwitchingRef.current = false;
          }
        }
      }
    }

    setActiveSection(prev => (prev === num ? null : num));
  }, [activeSection, can_edit]);

  const handleSaveNext = useCallback((currentNum: number) => {
    if (isSwitchingRef.current) return;
    if (currentNum >= 4) {
      setActiveSection(null);
    } else {
      setActiveSection(currentNum + 1);
    }
  }, []);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <VerdictBanner execution={execution} dynamic_checklist={dynamic_checklist} />

      <MarketSection
        date={date}
        account_number={account_number}
        symbol={symbol}
        journal_data={journal_data}
        form_configs={form_configs}
        ui_labels={ui_labels?.step_1}
        onReload={onReload}
        can_edit={can_edit}
        open={activeSection === 1}
        onToggle={() => handleToggle(1)}
        onSaveSuccess={() => handleSaveNext(1)}
        sectionRef={marketSectionRef}
      />
      <SetupSection
        date={date}
        account_number={account_number}
        symbol={symbol}
        journal_data={journal_data}
        form_configs={form_configs}
        ui_labels={ui_labels?.step_2}
        dynamic_checklist={dynamic_checklist}
        onReload={onReload}
        can_edit={can_edit}
        open={activeSection === 2}
        onToggle={() => handleToggle(2)}
        onSaveSuccess={() => handleSaveNext(2)}
        sectionRef={setupSectionRef}
      />
      <RiskSection
        date={date}
        account_number={account_number}
        symbol={symbol}
        journal_data={journal_data}
        form_configs={form_configs}
        ui_labels={ui_labels?.step_3}
        dynamic_checklist={dynamic_checklist}
        onReload={onReload}
        can_edit={can_edit}
        open={activeSection === 3}
        onToggle={() => handleToggle(3)}
        onSaveSuccess={() => handleSaveNext(3)}
        sectionRef={riskSectionRef}
      />
      <PsychologySection
        date={date}
        account_number={account_number}
        symbol={symbol}
        journal_data={journal_data}
        form_configs={form_configs}
        ui_labels={ui_labels?.step_4}
        dynamic_checklist={dynamic_checklist}
        can_edit={can_edit}
        onReload={onReload}
        open={activeSection === 4}
        onToggle={() => handleToggle(4)}
        onSaveSuccess={() => handleSaveNext(4)}
        sectionRef={psychSectionRef}
      />
    </div>
  );
});

export default StepBeforeEntry;